import { useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useUser } from "@clerk/clerk-react";
import { useTheme } from "@heroui/use-theme";

import { Navbar } from "@/layouts/navbar";
import MobileNav from "./mobile-nav";

const THEME_KEY = "theme"; // where we store theme in Clerk unprotected data

export default function DefaultLayout({ children }: { children: React.ReactNode }) {
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { user, isSignedIn, isLoaded } = useUser();
  const { theme, setTheme } = useTheme();

  // Hydrate theme from Clerk and default to "light" if missing
  useEffect(() => {
    if (!isLoaded) return;

    // If not signed in, just ensure app has a theme (default light)
    if (!isSignedIn || !user) {
      if (theme !== "light") setTheme("light");
      return;
    }

    const currentUnsafe = (user.unsafeMetadata || {}) as Record<string, unknown>;
    const saved = currentUnsafe[THEME_KEY];

    if (saved === "light" || saved === "dark") {
      // Only apply if different to avoid loops
      if (saved !== theme) setTheme(saved);
    } else {
      // No key: set to light both locally and in Clerk
      if (theme !== "light") setTheme("light");
      void user
        .update({
          unsafeMetadata: {
            ...currentUnsafe,
            [THEME_KEY]: "light",
          },
        })
        .catch((e) => console.error("Failed to seed theme in Clerk:", e));
    }
  }, [isLoaded, isSignedIn, user, theme, setTheme]);

  const NAV_H = "64px";
  const TAB_H = "64px";

  if (isMobile) {
    return (
      <div className="relative h-screen w-full flex flex-col">
        <div className="fixed top-0 left-0 right-0 z-50">
          <Navbar />
        </div>

        <div
          className="flex flex-col flex-1 pt-[70px] overflow-y-auto scroll-smooth"
          style={{ touchAction: "pan-y" }}
        >
          <main className="flex-1 min-h-0">
            <div className="container mx-auto max-w-7xl flex flex-col h-full min-h-0">
              <div className="flex-1 min-h-0 flex flex-col" id="page-content">
                {children}
              </div>
            </div>
          </main>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
          <div className="pointer-events-auto">
            <MobileNav />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-900">
      <div
        id="ui_root"
        className={`relative h-[844px] w-[390px] flex flex-col overflow-hidden
          rounded-[28px] border border-neutral-200 dark:border-neutral-700
          shadow-2xl ring-1 ring-black/5
          bg-white/90 dark:bg-neutral-900/90 backdrop-blur`}
      >
        <div className="absolute top-0 left-0 right-0 z-50" style={{ height: NAV_H }}>
          <Navbar />
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto scroll-smooth overscroll-contain"
          style={{
            paddingTop: NAV_H,
            paddingBottom: TAB_H,
            touchAction: "pan-y",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-50" style={{ height: TAB_H }}>
          <MobileNav />
        </div>
      </div>
    </div>
  );
}